import {
  Box,
  BoxProps,
  Circle,
  createIcon,
  Icon,
  Stack,
  StackProps,
  useId,
  useRadio,
  useRadioGroup,
  UseRadioProps,
  useStyleConfig,
} from '@chakra-ui/react';
import { Children, cloneElement, isValidElement, ReactElement, useMemo } from 'react';

interface SingleChoiceAnswerProps<T> extends Omit<StackProps, 'onChange'> {
  name?: string;
  value?: T;
  defaultValue?: string;
  onChange?: (value: T) => void;
}

export function SingleChoiceAnswer<T extends string>(props: SingleChoiceAnswerProps<T>) {
  const { children, name, defaultValue, value, onChange, ...rest } = props;
  const { getRootProps, getRadioProps } = useRadioGroup({
    name,
    defaultValue,
    value,
    onChange,
  });

  const cards = useMemo(
    () =>
      Children.toArray(children)
        .filter<ReactElement<SingleChoiceAnswerCardProps>>(isValidElement)
        .map((card) => {
          return cloneElement(card, {
            radioProps: getRadioProps({
              value: card.props.value,
            }),
          });
        }),
    [children, getRadioProps],
  );

  return <Stack {...getRootProps(rest)}>{cards}</Stack>;
}

interface SingleChoiceAnswerCardProps extends BoxProps {
  value: string;
  radioProps?: UseRadioProps;
  readOnly?: boolean;
}

export function SingleChoiceAnswerCard(props: SingleChoiceAnswerCardProps) {
  const { radioProps, children, readOnly = false, ...rest } = props;
  const { getInputProps, getRadioProps, getLabelProps, state } = useRadio(radioProps);
  const id = useId(undefined, 'radio-button');

  const styles = useStyleConfig('RadioCard', props);
  const inputProps = getInputProps();
  const checkboxProps = getRadioProps();
  const labelProps = getLabelProps();
  return (
    <Box
      as="label"
      cursor={readOnly ? 'default' : 'pointer'}
      {...labelProps}
      sx={{
        '.focus-visible + [data-focus]': {
          boxShadow: 'outline',
          zIndex: 1,
        },
      }}
    >
      <input {...inputProps} aria-labelledby={id} disabled={readOnly} />
      <Box sx={styles} {...checkboxProps} {...rest}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={4}>
          {state.isChecked ? (
            <Circle bg="brand.500" size="4">
              <Icon as={CheckIconCustom} boxSize="2.5" color="fg.inverted" />
            </Circle>
          ) : (
            <Circle borderWidth="2px" size="4" />
          )}
          <Box flex="1">{children}</Box>
        </Stack>
      </Box>
    </Box>
  );
}

export const CheckIconCustom = createIcon({
  displayName: 'CheckIcon',
  viewBox: '0 0 12 10',
  path: (
    <polyline
      fill="none"
      strokeWidth="2px"
      stroke="currentColor"
      strokeDasharray="16px"
      points="1.5 6 4.5 9 10.5 1"
    />
  ),
});
